import TrailerThumbnailItem from '@/app/[locale]/[roomId]/components/TrailerThumbnailItem'
import { Skeleton } from '@/components/ui/skeleton'
import { useTrailerViewMediaContext } from '@/context/TrailerViewMediaContext'
import { useUAContext } from '@/context/UAContext'
import useScrollFade from '@/hooks/useScrollFade'
import useThumbnailScroll from '@/hooks/useThumbnailScroll'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

const GAP = 12
const CARD_WIDTH_M = 142
const CARD_WIDTH_S = 100

const TrailerThumbnails = () => {
  const { isMobileLandscape } = useUAContext()
  const { mediaList, currentIndex, setCurrentIndex, isEmpty, isLoading } =
    useTrailerViewMediaContext()
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const thumbnailRef = useRef<(HTMLDivElement | null)[]>([])
  const lastHandledIndexRef = useRef<number | null>(null)
  const { canScrollLeft, canScrollRight, updateScrollFade } = useScrollFade(container)
  const [fullWidth, setFullWidth] = useState(0)
  const [imgErrorArr, setImgErrorArr] = useState<boolean[]>([])
  const scrollToThumbnail = useThumbnailScroll(container, thumbnailRef)
  const [thumbWidth, setThumbWidth] = useState(CARD_WIDTH_M)

  const handleClick = useCallback(
    (index: number) => {
      scrollToThumbnail(index, fullWidth, mediaList, setCurrentIndex)
    },
    [scrollToThumbnail, fullWidth, mediaList, setCurrentIndex],
  )

  useEffect(() => {
    setImgErrorArr(Array(mediaList.length).fill(false))
  }, [mediaList.length])

  const handleImgError = (idx: number) => {
    setImgErrorArr((prev) => {
      const next = [...prev]
      next[idx] = true
      return next
    })
  }

  const getVisibleThumbnail = (visibleCount: number, gap: number, containerWidth: number) => {
    const thumbWidth = (containerWidth - gap * (visibleCount - 1)) / visibleCount
    return thumbWidth
  }

  const updateThumbnailWidth = useCallback(() => {
    const screenWidth = window.innerWidth
    let _thumbWidth = CARD_WIDTH_M
    if (screenWidth >= 1440) {
      _thumbWidth = CARD_WIDTH_M
    } else if (screenWidth === 1024 || screenWidth === 768) {
      _thumbWidth = CARD_WIDTH_S
    } else if (screenWidth < 768 || isMobileLandscape) {
      _thumbWidth = getVisibleThumbnail(3.5, GAP, container?.clientWidth || 390)
    } else {
      _thumbWidth = getVisibleThumbnail(6.5, GAP, container?.clientWidth || 768)
    }
    setThumbWidth(_thumbWidth)
    setFullWidth(_thumbWidth + GAP)
    updateScrollFade()
  }, [container, isMobileLandscape, updateScrollFade])

  // update width when container resize
  useEffect(() => {
    if (!container) return
    const resizeObserver = new ResizeObserver(() => updateThumbnailWidth())
    resizeObserver.observe(container)
    updateThumbnailWidth()
    return () => resizeObserver.disconnect()
  }, [container, updateThumbnailWidth])

  // update width when orientation changes
  useEffect(() => {
    updateThumbnailWidth()
  }, [isMobileLandscape, updateThumbnailWidth])

  useEffect(() => {
    const el = container
    if (!el) return

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
      el.scrollLeft += e.deltaY
      e.preventDefault()
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    return () => el.removeEventListener('wheel', onWheel)
  }, [container])

  useEffect(() => {
    // Sync scroll and main view when currentIndex changes (triggered by auto-play or manual click)
    if (!isEmpty && container && thumbnailRef.current[currentIndex]) {
      if (lastHandledIndexRef.current !== currentIndex) {
        handleClick(currentIndex)
        lastHandledIndexRef.current = currentIndex
      }
    }
  }, [isEmpty, container, currentIndex, handleClick])

  const thumbnailBaseClass =
    'relative aspect-[16/9] min-w-[100px] flex-shrink-0 overflow-hidden rounded-lg'

  return (
    <>
      {mediaList.length > 0 && (
        <div className="relative w-full overflow-visible">
          <div
            ref={setContainer}
            className="no-scrollbar scrollbar-hide flex w-full max-w-full min-w-0 flex-row gap-3 overflow-x-auto pt-3 md:pt-4"
            style={{ scrollBehavior: 'smooth' }}
          >
            {mediaList.map((item, index) =>
              item.isPlaceholder && isLoading ? (
                <Skeleton
                  key={item.key || `placeholder-${index}`}
                  className={cn(
                    'bg-transparent bg-linear-to-tr from-slate-500/10 to-gray-800/10',
                    thumbnailBaseClass,
                  )}
                  style={{ width: thumbWidth }}
                />
              ) : (
                <TrailerThumbnailItem
                  key={item.key || `thumbnail-${index}`}
                  item={item}
                  index={index}
                  currentIndex={currentIndex}
                  thumbWidth={thumbWidth}
                  handleClick={handleClick}
                  thumbnailRef={thumbnailRef}
                  updateScrollFade={updateScrollFade}
                  imgErrorArr={imgErrorArr}
                  handleImgError={handleImgError}
                />
              ),
            )}
          </div>
          {canScrollLeft && (
            <div className="pointer-events-none absolute top-0 bottom-0 left-0 z-20 w-[calc(var(--thumbnail-width,142px)*2/3)] -translate-x-3 bg-gradient-to-r from-[#121216] to-transparent" />
          )}
          {canScrollRight && (
            <div className="pointer-events-none absolute top-0 right-0 bottom-0 z-20 w-[calc(var(--thumbnail-width,142px)*2/3)] translate-x-3 bg-gradient-to-l from-[#121216] to-transparent" />
          )}
        </div>
      )}
    </>
  )
}
export default TrailerThumbnails
